/**
 * Why an item is in the list, which sets its place:
 * - `blocking`: something is broken, or someone is waiting on you;
 * - `your-move`: your own work waits for your next step;
 * - `wrap-up`: ready to finish or tidy up;
 * - `background`: worth knowing, nothing to do today.
 */
export type NeedsYouPriority =
  | 'blocking'
  | 'your-move'
  | 'wrap-up'
  | 'background';

export const PRIORITY_ORDER: readonly NeedsYouPriority[] = [
  'blocking',
  'your-move',
  'wrap-up',
  'background',
];

/** The tone of an item's bar. Within a priority, the stronger tone leads. */
export type NeedsYouUrgency = 'critical' | 'warning' | 'normal' | 'low';

export const URGENCY_ORDER: readonly NeedsYouUrgency[] = [
  'critical',
  'warning',
  'normal',
  'low',
];

export type NeedsYouSourceId =
  | 'github-reviews'
  | 'github-my-prs'
  | 'dependency-security'
  | 'blueprints'
  | 'locks'
  | 'devboxes'
  | 'internal-apps'
  | 'deployments';

/** One thing waiting for the signed-in user. */
export type NeedsYouItem = {
  id: string;
  source: NeedsYouSourceId;
  priority: NeedsYouPriority;
  urgency: NeedsYouUrgency;
  /** Why it's here, shown above the title, e.g. "Review requested". */
  label: string;
  /** Where, who and when, e.g. "integromat/backstage #688 · by jane". */
  meta?: string;
  /** What it is, in words: a pull request's title, a package upgrade. */
  title: string;
  /** An extra line, such as the note on a lock. */
  detail?: string;
  action: { label: string; href: string; external?: boolean };
  /** Epoch ms used to break ties: older items first. */
  since?: number;
};

export type NeedsYouSourceState = {
  items: NeedsYouItem[];
  loading: boolean;
  error?: Error;
};

export const EMPTY_SOURCE: NeedsYouSourceState = { items: [], loading: false };
